import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import PostItemWrapper, { PostItemWrapperPreview } from 'molecules/post-items/PostItemWrapper'
import { ItemDataMap } from 'store/types'
import { useAtom } from 'jotai'
import { canvaPropsAtom, higherZIndexAtom, itemsDataAtom, selectedItemIdAtom } from 'store/atoms'
import PostItemText from 'molecules/post-items/text/PostItemText'
import PostItemImage from 'molecules/post-items/image/PostItemImage'
import PostItemShape from 'molecules/post-items/shape/PostItemShape'

export type PostRef = {
  mainRef: HTMLDivElement | null
  previewRef: HTMLDivElement | null
  itemsRef: (HTMLDivElement | null)[]
}

const Post = forwardRef<PostRef>((_, ref) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<PostRef['previewRef']>(null)
  const itemsRef = useRef<PostRef['itemsRef']>([])

  const [canvaProps] = useAtom(canvaPropsAtom)
  const [itemsData, setItemsData] = useAtom(itemsDataAtom)
  const [selectedItemId, setSelectedItemId] = useAtom(selectedItemIdAtom)
  const [higherZIndex, setHigherZIndex] = useAtom(higherZIndexAtom)

  useImperativeHandle(ref, () => ({
    mainRef: mainRef.current,
    previewRef: previewRef.current,
    itemsRef: itemsRef.current
  }))

  useEffect(() => {
    if (!(previewRef as any)?.current) throw new Error('[Post] Not passing ref as MutableRefObject')
  }, [previewRef])

  const makeTextResize = useCallback(
    (itemRef: HTMLDivElement) => {
      if (!mainRef.current) return

      const itemId = itemRef.getAttribute('data-id')

      if (itemId) {
        setItemsData(oldState => {
          if (
            !itemRef?.clientHeight ||
            !itemRef?.clientWidth ||
            !mainRef.current?.clientHeight ||
            !mainRef.current?.clientWidth
          ) {
            return oldState
          }

          const oldStateItem = oldState[itemId]

          return {
            ...oldState,
            [itemId]: {
              ...oldStateItem,
              // When changing here, be sure to change the same code in parentSizeChanged
              height: itemRef.style.height.endsWith('%')
                ? oldStateItem.height
                : `${(100 * itemRef?.clientHeight) / mainRef.current?.clientHeight}%`,
              width: itemRef.style.width.endsWith('%')
                ? oldStateItem.width
                : `${(100 * itemRef?.clientWidth) / mainRef.current?.clientWidth}%`
            }
          }
        })
      }
    },
    [mainRef.current]
  )

  const parentSizeChanged = useCallback(() => {
    if (!mainRef.current) return
    setItemsData(oldState => {
      const newState: ItemDataMap = {}

      Object.values(oldState).forEach(oldStateItem => {
        const currentParentWidth = mainRef.current?.clientWidth
        const currentParentHeight = mainRef.current?.clientHeight
        const oldParentWidth = oldStateItem.lastParentWidth || currentParentWidth
        const oldParentHeight = oldStateItem.lastParentHeight || currentParentHeight
        const x = (oldStateItem.x * (mainRef.current?.clientWidth || 1)) / (oldParentWidth || 1)
        const y = (oldStateItem.y * (mainRef.current?.clientHeight || 1)) / (oldParentHeight || 1)

        const itemRef = itemsRef.current.find(ref => ref && ref.getAttribute('data-id') === oldStateItem.id)

        newState[oldStateItem.id] = {
          ...oldStateItem,
          x,
          y,
          lastParentWidth: mainRef.current?.clientWidth || 0,
          lastParentHeight: mainRef.current?.clientHeight || 0,
          // This code fix the eventual issue where the item changes size and is not converted to percentage
          // When changing here, be sure to change the same code in makeTextResize
          ...(itemRef && mainRef.current?.clientHeight && mainRef.current?.clientWidth
            ? {
                height: itemRef.style.height.endsWith('%')
                  ? oldStateItem.height
                  : `${(100 * itemRef?.clientHeight) / mainRef.current?.clientHeight}%`,
                width: itemRef.style.width.endsWith('%')
                  ? oldStateItem.width
                  : `${(100 * itemRef?.clientWidth) / mainRef.current?.clientWidth}%`
              }
            : {})
        }
      })
      return newState
    })
  }, [mainRef.current])

  useEffect(() => {
    if (!itemsRef || !mainRef.current) return
    // parent
    const resizeParentObserver = new ResizeObserver(() => {
      parentSizeChanged()
    })
    resizeParentObserver.observe(mainRef.current)
    // children

    const childrenObservers = itemsRef.current.map(itemRef => {
      if (!itemRef) return
      const observer = new ResizeObserver(cb => {
        makeTextResize(cb[0].target as HTMLDivElement)
      })
      observer.observe(itemRef)
      return observer
    })
    return () => {
      childrenObservers.forEach(observer => observer?.disconnect())
      resizeParentObserver.disconnect()
    }
  }, [itemsRef.current.length, mainRef.current])

  const content = (
    <div ref={mainRef}>
      <div
        className='relative overflow-hidden'
        style={{
          aspectRatio: 4 / 5,
          width: '100%',
          backgroundColor: canvaProps.backgroundColor
        }}>
        {Object.values(itemsData).map((item, index) => (
          <PostItemWrapper
            zIndex={item.zIndex}
            key={`main-${item.id}`}
            id={item.id}
            ref={el => {
              itemsRef.current[index] = el
              if (!el) return
              return el
            }}
            selected={item.id === selectedItemId}
            data={item}
            parentSize={{
              width: mainRef.current?.clientWidth,
              height: mainRef.current?.clientHeight
            }}
            onMouseDown={() => {
              setSelectedItemId(item.id)
              const zIndex = higherZIndex + 1
              setItemsData(oldState => ({
                ...oldState,
                [item.id]: {
                  ...oldState[item.id],
                  zIndex
                }
              }))
              setHigherZIndex(zIndex)
            }}
            onItemDataChange={item =>
              setItemsData(oldState => ({
                ...oldState,
                [item.id]: {
                  ...oldState[item.id],
                  ...item
                }
              }))
            }>
            {(() => {
              switch (item.type) {
                case 'text':
                  return <PostItemText {...item.props} />
                case 'image':
                  return <PostItemImage {...item.props} />
                case 'shape':
                  return <PostItemShape {...item.props} />
              }
            })()}
          </PostItemWrapper>
        ))}
      </div>
    </div>
  )

  const hiddenContent = (
    <div>
      <div
        ref={previewRef}
        className='relative overflow-hidden'
        style={{
          aspectRatio: 4 / 5,
          width: 540,
          backgroundColor: canvaProps.backgroundColor
        }}>
        {Object.values(itemsData).map(item => {
          const itemRef = itemsRef.current.find(itemRef => itemRef?.getAttribute('data-id') === item.id)
          return (
            <PostItemWrapperPreview
              key={`preview-${item.id}`}
              data={item}
              parentSize={{
                width: mainRef.current?.clientWidth,
                height: mainRef.current?.clientHeight
              }}
              itemSizeAsPx={{
                width: itemRef?.offsetWidth,
                height: itemRef?.offsetHeight
              }}
              previewSize={{
                width: previewRef.current?.clientWidth,
                height: previewRef.current?.clientHeight
              }}>
              {(() => {
                switch (item.type) {
                  case 'text':
                    return <PostItemText {...item.props} />
                  case 'image':
                    return <PostItemImage {...item.props} />
                  case 'shape':
                    return <PostItemShape {...item.props} />
                }
              })()}
            </PostItemWrapperPreview>
          )
        })}
      </div>
    </div>
  )

  return (
    <>
      {content}
      <div className='relative h-px w-px overflow-auto'>
        <div
          style={{
            aspectRatio: 4 / 5,
            width: 540
          }}>
          {hiddenContent}
        </div>
      </div>
    </>
  )
})

Post.displayName = 'Post'

export default Post
